import { useEffect, useRef, useState } from 'react'
import { CUSTOMER_DISPLAY_BUTTON_POSITION_KEY } from 'Utils/constants'

const BUTTON_SIZE = 64
const DEFAULT_RIGHT = 24
const DEFAULT_BOTTOM = 56
const MENU_WIDTH = 128
const MENU_HEIGHT = 96
const DRAG_THRESHOLD = 3

const canUseWindow = () => typeof window !== 'undefined'

const getDefaultPositionAnchor = () => ({
  horizontalEdge: 'right',
  horizontalOffset: DEFAULT_RIGHT,
  verticalEdge: 'bottom',
  verticalOffset: DEFAULT_BOTTOM,
})

const clampPosition = position => {
  if (!canUseWindow()) return position

  const maxX = Math.max(0, window.innerWidth - BUTTON_SIZE)
  const maxY = Math.max(0, window.innerHeight - BUTTON_SIZE)

  return {
    x: Math.min(Math.max(position.x, 0), maxX),
    y: Math.min(Math.max(position.y, 0), maxY),
  }
}

const isValidPositionAnchor = positionAnchor =>
  ['left', 'right'].includes(positionAnchor?.horizontalEdge) &&
  ['top', 'bottom'].includes(positionAnchor?.verticalEdge) &&
  Number.isFinite(positionAnchor?.horizontalOffset) &&
  Number.isFinite(positionAnchor?.verticalOffset)

const getPositionFromAnchor = positionAnchor => {
  if (!canUseWindow() || !isValidPositionAnchor(positionAnchor)) return null

  const x =
    positionAnchor.horizontalEdge === 'left'
      ? positionAnchor.horizontalOffset
      : window.innerWidth - BUTTON_SIZE - positionAnchor.horizontalOffset

  const y =
    positionAnchor.verticalEdge === 'top'
      ? positionAnchor.verticalOffset
      : window.innerHeight - BUTTON_SIZE - positionAnchor.verticalOffset

  return clampPosition({ x, y })
}

const getAnchorFromPosition = position => {
  if (!canUseWindow()) return getDefaultPositionAnchor()

  const clampedPosition = clampPosition(position)
  const rightOffset = window.innerWidth - BUTTON_SIZE - clampedPosition.x
  const bottomOffset = window.innerHeight - BUTTON_SIZE - clampedPosition.y
  const horizontalEdge = clampedPosition.x <= rightOffset ? 'left' : 'right'
  const verticalEdge = clampedPosition.y <= bottomOffset ? 'top' : 'bottom'

  return {
    horizontalEdge,
    horizontalOffset:
      horizontalEdge === 'left' ? clampedPosition.x : rightOffset,
    verticalEdge,
    verticalOffset: verticalEdge === 'top' ? clampedPosition.y : bottomOffset,
  }
}

const getDefaultPosition = () => {
  if (!canUseWindow()) return null

  return getPositionFromAnchor(getDefaultPositionAnchor())
}

const getStoredPositionAnchor = () => {
  if (!canUseWindow()) return null

  try {
    const storedPosition = localStorage.getItem(
      CUSTOMER_DISPLAY_BUTTON_POSITION_KEY
    )
    if (!storedPosition) return null

    const parsedPosition = JSON.parse(storedPosition)

    return isValidPositionAnchor(parsedPosition) ? parsedPosition : null
  } catch {
    return null
  }
}

const getStoredPosition = () => {
  if (!canUseWindow()) return null

  const storedPositionAnchor = getStoredPositionAnchor()
  return storedPositionAnchor
    ? getPositionFromAnchor(storedPositionAnchor)
    : null
}

const savePosition = position => {
  const positionAnchor = getAnchorFromPosition(position)

  try {
    localStorage.setItem(
      CUSTOMER_DISPLAY_BUTTON_POSITION_KEY,
      JSON.stringify(positionAnchor)
    )
  } catch {
    // Ignore storage errors; the button still works during the session.
  }

  return positionAnchor
}

const getInitialDragState = () => ({
  isDragging: false,
  moved: false,
  startX: 0,
  startY: 0,
  originX: 0,
  originY: 0,
})

export default function useFloatingButtonPosition({ isOpen, onClick }) {
  const [position, setPosition] = useState(
    () => getStoredPosition() || getDefaultPosition()
  )
  const positionAnchorRef = useRef(
    getStoredPositionAnchor() || getDefaultPositionAnchor()
  )
  const dragRef = useRef(getInitialDragState())

  useEffect(() => {
    if (!canUseWindow()) return undefined

    const handleResize = () => {
      setPosition(currentPosition => {
        const nextPosition =
          getPositionFromAnchor(positionAnchorRef.current) ||
          currentPosition ||
          getDefaultPosition()

        return nextPosition
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const getCurrentPosition = () =>
    position || getStoredPosition() || getDefaultPosition()

  const getMenuClassName = () => {
    const classNames = ['menu']
    const currentPosition = getCurrentPosition()

    if (isOpen) classNames.push('open')

    if (currentPosition?.y < MENU_HEIGHT) {
      classNames.push('menu--below')
    }

    if (currentPosition?.x < MENU_WIDTH - BUTTON_SIZE) {
      classNames.push('menu--align-left')
    }

    return classNames.join(' ')
  }

  const handlePointerDown = event => {
    if (event.button !== undefined && event.button !== 0) return

    const currentPosition = getCurrentPosition()
    if (!currentPosition) return

    dragRef.current = {
      isDragging: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      originX: currentPosition.x,
      originY: currentPosition.y,
    }

    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const handlePointerMove = event => {
    const dragState = dragRef.current
    if (!dragState.isDragging) return

    const deltaX = event.clientX - dragState.startX
    const deltaY = event.clientY - dragState.startY

    const hasMoved =
      Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD

    if (!dragState.moved && !hasMoved) return
    dragState.moved = true

    setPosition(
      clampPosition({
        x: dragState.originX + deltaX,
        y: dragState.originY + deltaY,
      })
    )
  }

  const handlePointerUp = event => {
    const dragState = dragRef.current
    if (!dragState.isDragging) return

    event.currentTarget.releasePointerCapture?.(event.pointerId)

    const nextPosition = clampPosition({
      x: dragState.originX + event.clientX - dragState.startX,
      y: dragState.originY + event.clientY - dragState.startY,
    })

    dragRef.current = getInitialDragState()

    if (dragState.moved) {
      setPosition(nextPosition)
      positionAnchorRef.current = savePosition(nextPosition)
      return
    }

    onClick()
  }

  const handlePointerCancel = () => {
    dragRef.current = getInitialDragState()
  }

  const handleKeyDown = event => {
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    onClick()
  }

  return {
    position,
    menuClassName: getMenuClassName(),
    buttonDragHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerCancel,
      onKeyDown: handleKeyDown,
    },
  }
}
